'use client';

import { useEffect, useRef, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import type { MatchWithGames } from '@/app/[locale]/match/libs/scoreSheet';

export type LiveMatchStatus = 'loading' | 'ready' | 'error';

/**
 * A match kept current by realtime, with polling and catch-up as recovery. With `initialMatch`
 * (a server-rendered page) it goes live straight away. Without one it fetches first and goes live
 * only if the match is still being played, so a finished match costs one request and no channel.
 * The caller keys on the match id: a changed `matchId` is not handled here.
 */
export function useLiveMatch({
  matchId,
  initialMatch,
}: {
  matchId: string;
  initialMatch?: MatchWithGames | null;
}): { match: MatchWithGames | null; status: LiveMatchStatus } {
  const [match, setMatch] = useState<MatchWithGames | null>(initialMatch ?? null);
  const [failed, setFailed] = useState(false);
  const [completionConfirmed, setCompletionConfirmed] = useState(
    initialMatch?.status === 'completed',
  );

  // Raw table notifications have no joined games. Keep those while canonical
  // recovery loads them, and stop only after completion has been reconciled.
  const latestMatch = useRef(match);
  useEffect(() => {
    if (completionConfirmed) return;
    let disposed = false;
    let revision = 0;
    let request = 0;
    let connected = false;
    let recoveryNeeded = false;
    let needsFinalHydration = false;
    const refreshMatch = async () => {
      if (disposed || document.visibilityState === 'hidden') return;
      const requestedRevision = revision;
      const requestedId = ++request;
      try {
        const response = await fetch(`/api/match/${matchId}`);
        if (disposed || requestedRevision !== revision || requestedId !== request) return;
        if (!response.ok) {
          if (!latestMatch.current) setFailed(true);
          recoveryNeeded = true;
          startPolling();
          return;
        }
        const data = await response.json();
        if (disposed || requestedRevision !== revision || requestedId !== request) return;
        const canonical = data.match as MatchWithGames | undefined;
        if (!canonical || canonical.id !== matchId) {
          if (!latestMatch.current) setFailed(true);
          recoveryNeeded = true;
          startPolling();
          return;
        }
        const current = latestMatch.current;
        // A cached response cannot move an already completed match back to live.
        if (current?.status === 'completed' && canonical.status !== 'completed') {
          recoveryNeeded = true;
          needsFinalHydration = true;
          startPolling();
          return;
        }
        if (
          canonical.updated_at &&
          current?.updated_at &&
          Date.parse(canonical.updated_at) < Date.parse(current.updated_at)
        ) {
          recoveryNeeded = true;
          startPolling();
          return;
        }
        // Finalization writes the match row and joined games separately. A
        // completed snapshot missing declared games is not final yet. That holds
        // only for a match already on screen: some finished rows store fewer
        // games than they declare for good, and a first load must not wait on them.
        if (
          current &&
          canonical.status === 'completed' &&
          (!Array.isArray(canonical.match_game) ||
            canonical.match_game.length <
              (canonical.player_1_games ?? 0) + (canonical.player_2_games ?? 0))
        ) {
          recoveryNeeded = true;
          needsFinalHydration = true;
          startPolling();
          return;
        }
        const next = {
          ...current,
          ...canonical,
          match_game: canonical.match_game ?? current?.match_game,
        };
        latestMatch.current = next;
        recoveryNeeded = false;
        setMatch(next);
        if (connected && !needsFinalHydration) stopPolling();
        if (canonical.status === 'completed' && (!current || Array.isArray(canonical.match_game))) {
          stopPolling();
          setCompletionConfirmed(true);
        } else if (!current) {
          startChannel();
          startPolling();
        }
      } catch (error) {
        // Retain the last usable view and keep recovery alive after a failure.
        if (disposed || requestedRevision !== revision || requestedId !== request) return;
        if (!latestMatch.current) setFailed(true);
        recoveryNeeded = true;
        startPolling();
        console.error('Error refreshing match data:', error);
      }
    };

    let channel: ReturnType<ReturnType<typeof createClient>['channel']> | null = null;
    let channelEpoch = 0;
    const stopChannel = () => {
      channelEpoch++;
      connected = false;
      const previous = channel;
      channel = null;
      if (previous) void previous.unsubscribe();
    };
    const startChannel = () => {
      // No match yet: the first response decides whether there is anything to watch.
      if (disposed || channel || !latestMatch.current || document.visibilityState === 'hidden')
        return;
      const epoch = channelEpoch;
      channel = createClient()
        .channel(`scorecard-match-${matchId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'match',
            filter: `id=eq.${matchId}`,
          },
          (payload) => {
            if (disposed || epoch !== channelEpoch || !payload.new) return;
            revision++;
            const raw = payload.new as MatchWithGames;
            const next = {
              ...latestMatch.current,
              ...raw,
              match_game: latestMatch.current?.match_game,
            };
            latestMatch.current = next;
            setMatch(next);
            if (next.status === 'completed') {
              needsFinalHydration = true;
              startPolling();
              void refreshMatch();
            }
          },
        )
        .subscribe((status) => {
          if (disposed || epoch !== channelEpoch) return;
          connected = status === 'SUBSCRIBED';
          if (connected) {
            if (!recoveryNeeded && !needsFinalHydration) stopPolling();
            void refreshMatch();
          } else {
            if (status === 'CLOSED') stopChannel();
            startPolling();
          }
        });
    };
    let interval: ReturnType<typeof setInterval> | null = null;
    const stopPolling = () => {
      if (interval !== null) clearInterval(interval);
      interval = null;
    };
    const startPolling = () => {
      if (
        disposed ||
        interval !== null ||
        document.visibilityState === 'hidden' ||
        (connected && !recoveryNeeded && !needsFinalHydration)
      )
        return;
      interval = setInterval(() => {
        startChannel();
        void refreshMatch();
      }, 30000);
    };
    if (latestMatch.current) {
      startChannel();
      startPolling();
    } else {
      void refreshMatch();
    }
    const resume = () => {
      if (document.visibilityState !== 'hidden') {
        startChannel();
        startPolling();
        void refreshMatch();
      } else {
        revision++;
        stopPolling();
        stopChannel();
      }
    };
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('online', resume);
    return () => {
      disposed = true;
      stopPolling();
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('online', resume);
      stopChannel();
    };
  }, [matchId, completionConfirmed]);

  const status: LiveMatchStatus = match ? 'ready' : failed ? 'error' : 'loading';
  return { match, status };
}
